/**
 * Stripe.js, loaded from Stripe the one way Stripe supports: a script tag naming `js.stripe.com`.
 *
 * There is no npm dependency for it. Stripe requires the script to come from its own host and
 * updates it in place, so a bundled copy is unsupported and an `integrity` attribute would break on
 * Stripe's next release. What keeps any other script out is the deployed Content-Security-Policy,
 * whose `script-src` admits only Stripe's hosts beside Sigora's own.
 *
 * The release in the path is the one the server pins as `Stripe-Version` (`2026-08-26.dahlia`),
 * so the embedded form and the sessions it is mounted with speak the same version.
 */
export const STRIPE_JS_URL = 'https://js.stripe.com/dahlia/stripe.js';

/** The embedded Checkout form, once created: mounted into one element, and destroyed after. */
export interface EmbeddedCheckoutPage {
  mount(element: HTMLElement): void;
  destroy(): void;
}

export interface EmbeddedCheckoutPageOptions {
  /** Asked for the open session's client secret each time the form needs one. */
  fetchClientSecret: () => Promise<string>;
  /** Called once the payment is complete, for a session created with `if_required`. */
  onComplete?: () => void;
}

/** The part of a Stripe instance Sigora calls. */
export interface StripeInstance {
  createEmbeddedCheckoutPage(options: EmbeddedCheckoutPageOptions): Promise<EmbeddedCheckoutPage>;
}

/** The global the script defines: one Stripe instance per publishable key. */
export type StripeConstructor = (publishableKey: string) => StripeInstance;

declare global {
  interface Window {
    Stripe?: StripeConstructor;
  }
}

/** The script being loaded, while it is; a failed load is forgotten so the next ask tries again. */
let loading: Promise<StripeConstructor> | null = null;

/**
 * Stripe.js, injected once per page. Answers the global at once when it is already defined, and
 * otherwise adds the script tag and waits for it; every caller while it loads shares the one tag.
 * A script that fails to load — blocked, offline — rejects, and is removed, so asking again adds
 * a new tag rather than waiting on the dead one.
 */
export const loadStripe = (): Promise<StripeConstructor> => {
  if (window.Stripe !== undefined) {
    return Promise.resolve(window.Stripe);
  }

  if (loading !== null) {
    return loading;
  }

  const script = document.createElement('script');
  script.src = STRIPE_JS_URL;
  script.async = true;

  const loaded = new Promise<StripeConstructor>((resolve, reject) => {
    script.addEventListener('load', () => {
      if (window.Stripe === undefined) {
        reject(new Error('Stripe.js loaded without defining Stripe'));
      } else {
        resolve(window.Stripe);
      }
    });
    script.addEventListener('error', () => {
      reject(new Error('Stripe.js could not be loaded'));
    });
  }).catch((failure: unknown) => {
    script.remove();
    loading = null;
    throw failure;
  });

  loading = loaded;
  document.head.append(script);

  return loaded;
};
